import React, { Suspense, useState } from "react";
import { Canvas } from "@react-three/fiber";
import {
  OrbitControls,
  useGLTF,
  Stage,
  Environment,
  Center,
  Loader,
} from "@react-three/drei";
import { motion } from 'framer-motion';
import {
  Layout,
  ShoppingCart,
  Palette,
  Settings,
  Globe,
  CreditCard,
  Shield,
  BarChart,
  ShoppingBag,
  Lock,
  ArrowRight,
  X,
  Minus,
  Square,
  ExternalLink,
} from 'lucide-react';

// Préchargement du modèle
useGLTF.preload("/models/presta.glb");

function Model() {
  const { scene } = useGLTF("/models/presta.glb");
  return (
    <Center>
      <primitive
        object={scene}
        scale={[0.015, 0.015, 0.015]}
        position={[0, -1, 0]}
        rotation={[0, Math.PI / 4, 0]}
      />
    </Center>
  );
}

function ShopScene() {
  return (
    <Canvas
      shadows
      camera={{ position: [6, 3, 6], fov: 45 }}
      className="fixed inset-0">
      <color attach="background" args={["#00762F"]} />
      <Suspense fallback={null}>
        <Stage environment="city">
          <Model />
        </Stage>
        <Environment preset="city" />
        <OrbitControls autoRotate />
      </Suspense>
    </Canvas>
  );
}

// Composant BrowserWindow pour créer des fenêtres de visualisation
const BrowserWindow = ({ title, url, isOpen, onClose }) => {
  const [isMinimized, setIsMinimized] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      className="fixed inset-0 flex items-center justify-center z-50 p-4"
    >
      {/* Overlay sombre */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      
      <div className={`relative ${isMaximized ? 'w-full h-full' : 'w-11/12 max-w-5xl'} ${isMinimized ? 'h-auto' : ''}`}>
        <div className="bg-white rounded-lg shadow-2xl overflow-hidden">
          {/* Barre de titre */}
          <div className="bg-gradient-to-r from-purple-500 to-pink-600 px-4 py-2 flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <ShoppingCart className="w-4 h-4 text-white" />
              <span className="text-white font-medium text-sm">{title}</span>
            </div>
            <div className="flex items-center space-x-2">
              <button 
                onClick={() => setIsMinimized(!isMinimized)}
                className="w-3 h-3 bg-yellow-400 rounded-full hover:bg-yellow-500 transition-colors flex items-center justify-center"
              >
                <Minus className="w-2 h-2 text-yellow-800" />
              </button>
              <button 
                onClick={() => setIsMaximized(!isMaximized)}
                className="w-3 h-3 bg-green-400 rounded-full hover:bg-green-500 transition-colors flex items-center justify-center"
              >
                <Square className="w-2 h-2 text-green-800" />
              </button>
              <button 
                onClick={onClose}
                className="w-3 h-3 bg-red-400 rounded-full hover:bg-red-500 transition-colors flex items-center justify-center"
              >
                <X className="w-2 h-2 text-red-800" />
              </button>
            </div>
          </div>
          
          {/* Barre d'adresse */}
          {!isMinimized && (
            <div className="bg-gray-100 px-4 py-2 flex items-center space-x-2 border-b border-gray-200">
              <div className="flex-1 bg-white rounded-md px-3 py-1 text-sm text-gray-600 flex items-center justify-between">
                <span>{url}</span>
                <a href={url} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="w-3 h-3 text-gray-400 hover:text-gray-600" />
                </a>
              </div>
            </div>
          )}
          
          {/* Contenu - iframe */}
          {!isMinimized && (
            <div className="bg-gray-50" style={{ height: isMaximized ? 'calc(100vh - 120px)' : '600px' }}>
              <iframe 
                src={url} 
                className="w-full h-full"
                title={title}
              />
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

const PrestaShop = () => {
  const [openWindows, setOpenWindows] = useState({});

  const toggleWindow = (key) => {
    setOpenWindows(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const features = [
    {
      icon: <ShoppingCart className="w-6 h-6" />,
      title: 'Commerce Performant',
      description: 'Gérez votre boutique en ligne avec des outils de vente puissants.'
    },
    {
      icon: <Palette className="w-6 h-6" />,
      title: 'Design Personnalisé',
      description: 'Personnalisez l\'apparence de votre boutique selon votre marque.'
    },
    {
      icon: <Layout className="w-6 h-6" />,
      title: 'Interface Intuitive',
      description: 'Une administration simple pour gérer vos produits et commandes.'
    },
    {
      icon: <Settings className="w-6 h-6" />,
      title: 'Configuration Flexible',
      description: 'Adaptez votre boutique à vos besoins spécifiques.'
    },
    {
      icon: <CreditCard className="w-6 h-6" />,
      title: 'Paiements Sécurisés',
      description: 'Intégrez de multiples solutions de paiement en toute sécurité.'
    },
    {
      icon: <Globe className="w-6 h-6" />,
      title: 'Multi-langues',
      description: 'Vendez dans le monde entier avec le support multilingue.'
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: 'Sécurité Avancée',
      description: 'Protégez votre boutique et vos clients avec des mesures de sécurité robustes.'
    },
    {
      icon: <BarChart className="w-6 h-6" />,
      title: 'Analyses Détaillées',
      description: 'Suivez vos performances avec des statistiques avancées.'
    }
  ];

  const prestashopLinks = [
    {
      key: 'ps-24-26',
      label: 'PrestaShop 2024-2026',
      url: 'https://web.cfa-montargis.com/prestashop-24-26/index.php',
      icon: <ShoppingBag className="w-5 h-5 text-purple-400 mr-3" />,
      promotion: 'Promotion 2024-2026'
    },
    {
      key: 'ps-admin-24-26',
      label: 'Admin PrestaShop 2024-2026',
      url: 'https://web.cfa-montargis.com/prestashop-24-26/admin583bs27uonvkzj2cil7i/',
      icon: <Lock className="w-5 h-5 text-purple-400 mr-3" />,
      promotion: 'Promotion 2024-2026'
    },
    {
      key: 'ps-25-27',
      label: 'PrestaShop 2025-2027',
      url: 'https://web.cfa-montargis.com/prestashop-25-27/index.php',
      icon: <ShoppingBag className="w-5 h-5 text-purple-400 mr-3" />,
      promotion: 'Promotion 2025-2027'
    },
    {
      key: 'ps-admin-25-27',
      label: 'Admin PrestaShop 2025-2027',
      url: 'https://web.cfa-montargis.com/prestashop-25-27/admin777hdm0rju2kswv1bfs/',
      icon: <Lock className="w-5 h-5 text-purple-400 mr-3" />,
      promotion: 'Promotion 2025-2027'
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="min-h-screen pt-32">
      <div className="fixed inset-0 z-0">
        <ShopScene />
      </div>

      <Loader />

      {/* Fenêtres de visualisation */}
      {prestashopLinks.map(link => (
        <BrowserWindow
          key={link.key}
          title={link.label}
          url={link.url}
          isOpen={openWindows[link.key]}
          onClose={() => toggleWindow(link.key)}
        />
      ))}

      <div className="container mx-auto px-4 relative z-10">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="text-center mb-16"
        >
          <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500">
            Créez votre boutique avec PrestaShop
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto bg-slate-800/60 p-4 rounded-lg backdrop-blur-sm">
            La solution e-commerce open source leader pour lancer votre boutique en ligne
          </p>
        </motion.div>

        {/* Features Grid avec animations d'icônes */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ scale: 1.05 }}
              className="bg-slate-800/60 backdrop-blur-sm rounded-lg p-6 hover:bg-slate-700/60 transition-all duration-300"
            >
              <motion.div 
                className="text-purple-400 mb-4"
                whileHover={{ rotate: 360 }}
                transition={{ duration: 0.5 }}
              >
                {feature.icon}
              </motion.div>
              <h3 className="text-xl font-semibold text-white mb-2">
                {feature.title}
              </h3>
              <p className="text-gray-300">
                {feature.description}
              </p>
            </motion.div>
          ))}
        </div>

        {/* CTA Section avec les liens PrestaShop */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="text-center mt-16 mb-32"
        >
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-8 rounded-lg max-w-5xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold mb-8 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500">
              Accéder aux plateformes PrestaShop
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.4 }}
                className="bg-slate-800/70 backdrop-blur-sm rounded-lg p-6 border border-slate-700"
              >
                <h3 className="text-xl font-semibold mb-4 text-pink-400">
                  Promotion 2024-2026
                </h3>
                <div className="space-y-3">
                  {prestashopLinks
                    .filter(link => link.promotion === 'Promotion 2024-2026')
                    .map(link => (
                      <div key={link.key} className="group">
                        <div className="flex items-center bg-slate-700/50 hover:bg-slate-700/80 p-3 rounded-lg transition-all duration-200">
                          <motion.div
                            whileHover={{ scale: 1.2, rotate: 360 }}
                            transition={{ duration: 0.3 }}
                          >
                            {link.icon}
                          </motion.div>
                          <span className="text-white flex-1">{link.label}</span>
                          <button
                            onClick={() => toggleWindow(link.key)}
                            className="text-blue-400 hover:text-blue-300 px-3 py-1 text-sm rounded bg-slate-600/50 hover:bg-slate-600 mr-2 transition-all"
                          >
                            Aperçu
                          </button>
                          <a
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <ArrowRight className="w-4 h-4 text-white hover:text-purple-400 transition-colors" />
                          </a>
                        </div>
                      </div>
                    ))}
                </div>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.5 }}
                className="bg-slate-800/70 backdrop-blur-sm rounded-lg p-6 border border-slate-700"
              >
                <h3 className="text-xl font-semibold mb-4 text-pink-400">
                  Promotion 2025-2027
                </h3>
                <div className="space-y-3">
                  {prestashopLinks
                    .filter(link => link.promotion === 'Promotion 2025-2027')
                    .map(link => (
                      <div key={link.key} className="group">
                        <div className="flex items-center bg-slate-700/50 hover:bg-slate-700/80 p-3 rounded-lg transition-all duration-200">
                          <motion.div
                            whileHover={{ scale: 1.2, rotate: 360 }}
                            transition={{ duration: 0.3 }}
                          >
                            {link.icon}
                          </motion.div>
                          <span className="text-white flex-1">{link.label}</span>
                          <button
                            onClick={() => toggleWindow(link.key)}
                            className="text-blue-400 hover:text-blue-300 px-3 py-1 text-sm rounded bg-slate-600/50 hover:bg-slate-600 mr-2 transition-all"
                          >
                            Aperçu
                          </button>
                          <a
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <ArrowRight className="w-4 h-4 text-white hover:text-purple-400 transition-colors" />
                          </a>
                        </div>
                      </div>
                    ))}
                </div>
              </motion.div>
            </div>
            
            <p className="text-lg mt-8 text-white/90">
              Commencez à vendre en ligne avec nos formateurs PrestaShop
            </p>
            
            <motion.a
              href="https://web.cfa-montargis.com/prestashop/admin1555cd5jgbbeuy0uobj/index.php?controller=AdminLogin&token=3f9734569720aee376fb61bc6119c3cc&redirect=AdminDashboard"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-block mt-6 bg-white text-purple-600 px-8 py-3 rounded-lg font-semibold hover:bg-opacity-90 transition-colors"
            >
              Démarrer maintenant
            </motion.a>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default PrestaShop;